/*****************************************************************
 *  CUSTOMER BUTLER - THE LIST IN HR'S ROSTER PATTERN              *
 *  File: apps/web/src/pages/customer/HrRecordList.js              *
 *  What it does: the customer list and the property list drawn    *
 *  the way HR Butler draws its roster:                            *
 *    1. full-width search with the primary button on its right    *
 *    2. a chip row for Zoho's saved views (HR's filter chips)     *
 *    3. the count line (handed in by the page)                    *
 *    4. HR's table on a wide screen - name with the email under   *
 *       it, one chip per cell, muted text, actions on the right - *
 *       and HR's cards on a phone (no sideways scroll at 390px)   *
 *  The search pause, views, sorting, paging and the no-signal     *
 *  copy are the SAME code the clean list uses (useListPages).     *
 *  2026-10-15 (Zoho tour frame 080): Zoho's left filter rail,     *
 *  Table | Cards, "Records per page", "Total Records", and the    *
 *  last change on every row (ZohoListParts.js).                   *
 *  Reads: the list door it is handed.  Writes: nothing.           *
 *****************************************************************/

/* WHY (2026-10-15, James): "Customer list and property list = HR's roster
   pattern." Same type sizes, paddings, line colours and hover as HR; the
   values all come from butler-tokens.css through customer.css section 16. */

/*****************************************************************
 *  TABLE OF CONTENTS                                              *
 *  SECTION 1 - SMALL PIECES (initials, a row's other words)       *
 *  SECTION 2 - ONE ROW: TABLE LINE AND PHONE CARD                 *
 *  SECTION 3 - SEARCH, VIEW CHIPS, SORT                           *
 *  SECTION 4 - THE WHOLE LIST                                     *
 *****************************************************************/

import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Icon } from './customerIcons';
import { StatusChip } from './customerParts';
import { HrBanner, HrQuietLine } from './HrChrome';
import { useListPages, readCellWords } from './RecordList';
import { formatMoment } from './customerDates';
import {
  FilterRail, PhoneFilterButton, AppliedFilterChips, ViewTypeSwitch, PerPagePicker, ZohoCardRow,
  useViewType, VIEW_TYPE_CARDS,
} from './ZohoListParts';

/*****************************************************************
 *  SECTION 1 - SMALL PIECES                                       *
 *****************************************************************/

const BLANK_MARK = '—'; // what an empty cell shows, like HR's "Nothing set" muted words

// Which icon each Zoho saved view wears on its chip.
const VIEW_ICONS = {
  all: 'people',
  'mailing-labels': 'file',
  mine: 'user',
  'new-last-week': 'calendar',
  'new-this-week': 'calendar',
  'recently-created': 'plus',
  'recently-modified': 'pencil',
  unsubscribed: 'close',
};

/* ---- initialsFor(nameWords) -- "Dana Example" -> "DE" (HR's avatar) ---- */
export function initialsFor(nameWords) {
  const nameParts = String(nameWords || '').trim().split(/\s+/).filter(Boolean);
  const firstLetter = nameParts.length > 0 ? nameParts[0].charAt(0) : '';
  const lastLetter = nameParts.length > 1 ? nameParts[nameParts.length - 1].charAt(0) : '';

  return (firstLetter + lastLetter).toUpperCase() || '?';
}

/* ---- isShownElsewhere(column, shownElsewhereKeys) ----------------
   True for the columns the name cell or the chip cell already
   shows, so the table never says the same fact twice on a line. */
function isShownElsewhere(column, shownElsewhereKeys) {
  return (shownElsewhereKeys || []).includes(column.key);
}

/*****************************************************************
 *  SECTION 2 - ONE ROW                                            *
 *****************************************************************/

/* ---- RowChips({ row, chipsFor }) -- the row's chips, plus "Not synced yet" ---- */
function RowChips({ row, chipsFor }) {
  const isWaitingToSync = row._syncStatus === 'pending';
  const pageChips = chipsFor ? chipsFor(row) : [];

  return (
    <>
      {pageChips.map((oneChip) => (
        <StatusChip key={oneChip.word} tone={oneChip.tone} icon={oneChip.icon} word={oneChip.word} />
      ))}
      {isWaitingToSync ? <StatusChip tone="warn" icon="offline" word="Not synced yet" /> : null}
    </>
  );
}

/* ---- TableLine(lineParts) -- one person/place on HR's table ----
   The whole line opens the record (click, Enter); the Open button
   on the right does the same for anyone who looks for a button. */
function TableLine(lineParts) {
  const { row, otherColumns, nameFor, underFor, chipsFor, linkFor, chipHeading } = lineParts;
  const navigate = useNavigate();
  const isWaitingToSync = row._syncStatus === 'pending';
  const rowLink = isWaitingToSync ? null : linkFor(row);
  const nameWords = nameFor(row);
  const underWords = underFor(row);

  /* ---- openOnEnter(keyEvent) -- the keyboard opens the line too ---- */
  function openOnEnter(keyEvent) {
    if (keyEvent.key === 'Enter' && rowLink) {
      navigate(rowLink);
    }
  }

  return (
    <tr
      className={rowLink ? 'cb-hr-trow' : 'cb-hr-trow is-static'}
      tabIndex={rowLink ? 0 : -1}
      onClick={rowLink ? () => navigate(rowLink) : undefined}
      onKeyDown={openOnEnter}
    >
      <td className="cb-hr-col-name">
        <span className="cb-hr-name-cell">
          <span className="cb-hr-avatar is-small" aria-hidden="true">{initialsFor(nameWords)}</span>
          <span className="cb-hr-name-words">
            <span className="cb-hr-person-name">{nameWords}</span>
            {underWords ? <span className="cb-hr-person-mail">{underWords}</span> : null}
          </span>
        </span>
      </td>
      {chipHeading ? (
        <td className="cb-hr-col-chips"><span className="cb-hr-chips"><RowChips row={row} chipsFor={chipsFor} /></span></td>
      ) : null}
      {otherColumns.map((oneColumn) => {
        const cellWords = readCellWords(row, oneColumn);

        return (
          <td key={oneColumn.key} className={cellWords ? 'cb-hr-col-text' : 'cb-hr-col-text is-blank'}>
            {cellWords || BLANK_MARK}
          </td>
        );
      })}
      <td className="cb-hr-col-text cb-hr-col-when">{formatMoment(row.updatedAt) || BLANK_MARK}</td>
      <td className="cb-hr-col-acts">
        {rowLink ? (
          <Link
            className="bc-btn is-quiet bc-btn-auto cb-hr-btn-sm"
            to={rowLink}
            onClick={(clickEvent) => clickEvent.stopPropagation()}
          >
            Open
          </Link>
        ) : null}
      </td>
    </tr>
  );
}

/* ---- PhoneCard(cardParts) -- the same row as HR's phone card ---- */
function PhoneCard(cardParts) {
  const { row, otherColumns, nameFor, underFor, chipsFor, linkFor } = cardParts;
  const isWaitingToSync = row._syncStatus === 'pending';
  const rowLink = isWaitingToSync ? null : linkFor(row);
  const nameWords = nameFor(row);
  const underWords = underFor(row);
  const footWords = otherColumns
    .map((oneColumn) => readCellWords(row, oneColumn))
    .filter(Boolean)
    .join(' · ');

  const cardInside = (
    <>
      <span className="cb-hr-avatar" aria-hidden="true">{initialsFor(nameWords)}</span>
      <span className="cb-hr-person-main">
        <span className="cb-hr-person-name">{nameWords}</span>
        {underWords ? <span className="cb-hr-person-mail">{underWords}</span> : null}
        <span className="cb-hr-chips"><RowChips row={row} chipsFor={chipsFor} /></span>
        {footWords ? <span className="cb-hr-person-foot">{footWords}</span> : null}
      </span>
    </>
  );

  if (!rowLink) {
    return <li><div className="cb-hr-person">{cardInside}</div></li>;
  }

  return <li><Link className="cb-hr-person" to={rowLink}>{cardInside}</Link></li>;
}

/*****************************************************************
 *  SECTION 3 - SEARCH, VIEW CHIPS, SORT                           *
 *****************************************************************/

/* ---- SearchAndAdd({ typedSearch, onType, searchHint, addButton }) ----
   HR's "hunt" row: the search box takes the width, the primary
   button sits on its right (it wraps under it on a phone).      */
function SearchAndAdd({ typedSearch, onType, searchHint, addButton }) {
  return (
    <div className="cb-hr-hunt">
      <label className="cb-hr-search-wrap">
        <span className="cb-hidden-label">Search</span>
        <Icon name="search" />
        <input
          className="cb-hr-search"
          type="search"
          value={typedSearch}
          placeholder={searchHint}
          onChange={(changeEvent) => onType(changeEvent.target.value)}
        />
      </label>
      {addButton || null}
    </div>
  );
}

/* ---- ViewChips({ views, chosenView, onChooseView, sorts, chosenSort, onChooseSort }) ----
   Zoho's saved views as HR's filter chips; the sort is a quiet
   select at the end of the same row (HR's look picker style).  */
function ViewChips({ views, chosenView, onChooseView, sorts, chosenSort, onChooseSort }) {
  return (
    <div className="cb-hr-tabs" role="tablist" aria-label="Saved views">
      {views.map((oneView) => (
        <button
          key={oneView.key}
          type="button"
          role="tab"
          className="cb-hr-tab"
          aria-selected={oneView.key === chosenView ? 'true' : 'false'}
          onClick={() => onChooseView(oneView.key)}
        >
          <Icon name={VIEW_ICONS[oneView.key] || 'file'} />
          {oneView.label}
        </button>
      ))}
      <label className="cb-hr-sort">
        <span className="cb-hidden-label">Sort</span>
        <select className="cb-hr-look" value={chosenSort} onChange={(changeEvent) => onChooseSort(changeEvent.target.value)}>
          {sorts.map((oneSort) => (
            <option key={oneSort.key} value={oneSort.key}>{oneSort.label}</option>
          ))}
        </select>
      </label>
    </div>
  );
}

/*****************************************************************
 *  SECTION 4 - THE WHOLE LIST                                     *
 *****************************************************************/

/* ---- HrRecordList(listParts) -------------------------------------
   In: { layout, fetchPage, fetchOffline (optional), linkFor(row),
         nameFor(row), underFor(row) (the muted line under the name),
         shownElsewhereKeys (columns the name or chip cell shows),
         chipHeading + chipsFor(row) (one chip each: tone, icon,
         word), searchHint, nounOne, nounMany, addButton (node),
         countLine (node, from the page), banner (node or null),
         railTitle, filterFields ([{ key, label }] or null when the
         list door has no field filters), cardLines ([{ label,
         column }] for the Cards view) }.
   Out: the roster-pattern list.                                 */
export function HrRecordList(listParts) {
  const { layout, linkFor, nameFor, underFor, shownElsewhereKeys, chipHeading, chipsFor } = listParts;
  const { searchHint, nounOne, nounMany, addButton, countLine, banner } = listParts;
  const { railTitle, filterFields, cardLines } = listParts;

  const {
    typedSearch, setTypedSearch, search, view, chooseView, sort, chooseSort,
    page, setPage, rows, isOfflineCopy, loadProblem, columns, totalCount, totalPages,
    perPage, choosePerPage, fieldFilters, applyFieldFilters,
  } = useListPages(listParts);
  const [viewType, chooseViewType] = useViewType(); // Table (HR, default) | Cards (Zoho)
  const isCards = viewType === VIEW_TYPE_CARDS;
  const railParts = {
    title: railTitle || 'Filter by',
    views: layout.listViews,
    chosenView: view,
    onChooseView: chooseView,
    fields: filterFields || null,
    appliedFilters: fieldFilters,
    onApply: applyFieldFilters,
  };

  const otherColumns = columns.filter((oneColumn) => !isShownElsewhere(oneColumn, shownElsewhereKeys));
  const firstColumnLabel = columns.length > 0 ? columns[0].label : 'Name';
  const shownWords = `${totalCount} ${totalCount === 1 ? nounOne : nounMany}`;
  const viewWords = (layout.listViews.find((oneView) => oneView.key === view) || {}).label || '';
  const rowParts = { otherColumns, nameFor, underFor, chipsFor, linkFor, chipHeading };

  return (
    <>
      {banner || null}

      <div className="cb-zl">
        <FilterRail {...railParts} />
        <div className="cb-zl-main">
          <div className="cb-hr-stick">
            <SearchAndAdd typedSearch={typedSearch} onType={setTypedSearch} searchHint={searchHint} addButton={addButton} />
            <ViewChips
              views={layout.listViews}
              chosenView={view}
              onChooseView={chooseView}
              sorts={layout.sorts}
              chosenSort={sort}
              onChooseSort={chooseSort}
            />
            <div className="cb-zl-toolbar">
              <PhoneFilterButton {...railParts} />
              <ViewTypeSwitch viewType={viewType} onChoose={chooseViewType} />
              <PerPagePicker perPage={perPage} onChoose={choosePerPage} />
            </div>
          </div>

          <AppliedFilterChips filters={fieldFilters} onClear={() => applyFieldFilters([])} />

          {countLine || null}

          {isOfflineCopy ? (
            <HrBanner tone="warn" icon="offline">
              No signal: showing the copy saved on this device. Views and sorting come back with the connection.
            </HrBanner>
          ) : null}
          {loadProblem ? <HrBanner tone="bad" icon="warn">{loadProblem}</HrBanner> : null}

          {rows === null ? <HrQuietLine icon="clock">Loading the {nounMany}</HrQuietLine> : null}

          {rows !== null && rows.length === 0 && !loadProblem ? (
            <HrQuietLine icon="search">
              {search ? `No ${nounMany} match "${search}".` : `No ${nounMany} in this view yet.`}
            </HrQuietLine>
          ) : null}

          {rows !== null && rows.length > 0 ? (
            <>
              <p className="cb-hr-count">{shownWords} in {viewWords}</p>

              {isCards ? (
                <ul className="cb-zl-cards" aria-label={nounMany}>
                  {rows.map((oneRow) => (
                    <ZohoCardRow key={oneRow.id} row={oneRow} nameFor={nameFor} cardLines={cardLines || []} linkFor={linkFor} />
                  ))}
                </ul>
              ) : null}

              <div className={isCards ? 'cb-hr-tablewrap is-off' : 'cb-hr-tablewrap'}>
                <table className="cb-hr-table">
                  <thead>
                    <tr>
                      <th scope="col" className="cb-hr-col-name">{firstColumnLabel}</th>
                      {chipHeading ? <th scope="col" className="cb-hr-col-chips">{chipHeading}</th> : null}
                      {otherColumns.map((oneColumn) => (
                        <th key={oneColumn.key} scope="col" className="cb-hr-col-text">{oneColumn.label}</th>
                      ))}
                      <th scope="col" className="cb-hr-col-text">Last changed</th>
                      <th scope="col" className="cb-hr-col-acts">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((oneRow) => <TableLine key={oneRow.id} row={oneRow} {...rowParts} />)}
                  </tbody>
                </table>
              </div>

              <ul className={isCards ? 'cb-hr-list is-off' : 'cb-hr-list'} aria-label={nounMany}>
                {rows.map((oneRow) => <PhoneCard key={oneRow.id} row={oneRow} {...rowParts} />)}
              </ul>

              {totalPages > 1 ? (
                <div className="cb-pager">
                  <button type="button" className="bc-btn is-ghost bc-btn-auto" disabled={page <= 1} onClick={() => setPage((pageNow) => pageNow - 1)}>
                    <Icon name="back" /> Previous
                  </button>
                  <span>Page {page} of {totalPages}</span>
                  <button type="button" className="bc-btn is-ghost bc-btn-auto" disabled={page >= totalPages} onClick={() => setPage((pageNow) => pageNow + 1)}>
                    Next <Icon name="chevron" />
                  </button>
                </div>
              ) : null}
            </>
          ) : null}

          {rows !== null ? <p className="cb-zl-total">Total Records {totalCount.toLocaleString('en-US')}</p> : null}
        </div>
      </div>
    </>
  );
}

export default HrRecordList;
